<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { Icon, Select, ProfilePicture } from '@components';
import DetailsModal from './DetailsModal.vue';
import MoreListModal from './MoreListModal.vue';
import { sortedDays, toRGB } from '@utils';
import { useRoute, useRouter } from 'vue-router';
import { useAppointment } from '@store/appointment';
import { useService } from '@store/service';
import { useStaff } from '@store/staff';
import { useSettings } from '@store/settings';
import moment from 'moment';

const route = useRoute();
const router = useRouter();
const $appointment = useAppointment();
const $service = useService();
const $staff = useStaff();
const $settings = useSettings();

import useDetailsModal from './useDetailsModal.js';
const { isDetailsModalActive, activeAppointmentId, openDetailsModal, closeDetailsModal, calculateDetailsModalPosition, calculateArrowPosition } = useDetailsModal();

onMounted(async () => {
    await $appointment.fetchData();
    await $service.fetchData();
    await $staff.fetchData();
});

const filter = ref({
    service: 'All',
    staff: 'All'
});

onMounted(() => {
    if (localStorage.getItem('userType') == 'staff') filter.value.staff = prebook_admin.profile.id;

});

const periods = ['Day', 'Week', 'Month'];

const period = ref('Month');

const changeTab = (tab) => {
    period.value = tab;
    router.replace({ query: { ...route.query, tab: tab } });
};

onMounted(() => {
    if (route.query.tab) period.value = route.query.tab;
    if (['Day', 'Week'].includes(period.value)) {
        // Scroll to .current-time-bar 
        setTimeout(() => {
            const currentTimeBar = document.querySelector('.current-time-bar');
            // console.log(period.value, currentTimeBar);
            if (currentTimeBar) currentTimeBar.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
    }
});

const serviceOptions = computed(() => {
    let options = $service.getServices.map(s => {
        return {
            label: s.name,
            value: s.service_id
        };
    }) || [];

    return [
        { label: `All ${$settings.labels.service?.plural}`, value: 'All' },
        ...options
    ];
});

const useCalendar = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const activeDate = ref(moment().format('YYYY-MM-DD'));
    const activeCalender = ref({
        month: 1, // Specify the month (1 for January, 2 for February, etc.)
        year: 0, // Specify the year
        dates: [],
    });

    // generate month dates
    const generateMonthDates = () => {
        const firstDayOfMonth = moment(`${activeCalender.value.year}-${activeCalender.value.month}-01`, 'YYYY-MM-DD');
        const lastDayOfMonth = moment(firstDayOfMonth).endOf('month');

        const currentDate = moment(firstDayOfMonth);
        const startDayOfCalenderWeek = sortedDays()[0];
        const endDayOfCalenderWeek = sortedDays()[sortedDays().length - 1];

        activeCalender.value.dates = [];

        // calculate the number of days to add at the beginning
        const daysToAddAtStart = (currentDate.day() + 7 - days.indexOf(startDayOfCalenderWeek)) % 7;

        // add blank objects at the beginning
        let dateStartOfLastMonth = firstDayOfMonth.subtract(daysToAddAtStart, 'days');
        for (let i = 0; i < daysToAddAtStart; i++) {
            activeCalender.value.dates.push({
                day: dateStartOfLastMonth.format('dddd'),
                date: dateStartOfLastMonth.date(),
                month: dateStartOfLastMonth.format('MMMM'),
                year: dateStartOfLastMonth.format('YYYY'),
                fullDate: dateStartOfLastMonth.format('YYYY-MM-DD')
            });

            dateStartOfLastMonth.add(1, 'days');
        }

        while (currentDate.isSameOrBefore(lastDayOfMonth)) {
            activeCalender.value.dates.push({
                day: currentDate.format('dddd'),
                date: currentDate.date(),
                month: currentDate.format('MMMM'),
                year: currentDate.format('YYYY'),
                fullDate: currentDate.format('YYYY-MM-DD'),
            });

            currentDate.add(1, 'days');
        }

        // calculate the number of days to add at the end
        const daysToAddAtEnd = ((days.indexOf(endDayOfCalenderWeek) + 7 - currentDate.day()) % 7) + 1;

        // add blank objects at the end
        if (daysToAddAtEnd > 6) return;
        let dateEndOfLastMonth = lastDayOfMonth.add(1, 'days');
        for (let i = 0; i < daysToAddAtEnd; i++) {
            activeCalender.value.dates.push({
                day: dateEndOfLastMonth.format('dddd'),
                date: dateEndOfLastMonth.date(),
                month: dateEndOfLastMonth.format('MMMM'),
                year: dateEndOfLastMonth.format('YYYY'),
                fullDate: dateEndOfLastMonth.format('YYYY-MM-DD')
            });

            dateEndOfLastMonth.add(1, 'days');
        }
    };

    // generate week dates
    const generateWeekDates = () => {
        const selectedDate = moment(activeDate.value, 'YYYY-MM-DD');
        const startDayOfCalenderWeek = sortedDays()[0];

        activeCalender.value.dates = [];

        let dayToAddStart = (selectedDate.day() + 7 - days.indexOf(startDayOfCalenderWeek)) % 7;
        let dateStart = selectedDate.subtract(dayToAddStart, 'days');

        for (let i = 0; i < 7; i++) {
            activeCalender.value.dates.push({
                day: dateStart.format('dddd'),
                date: dateStart.date(),
                month: dateStart.format('MMMM'),
                year: dateStart.format('YYYY'),
                fullDate: dateStart.format('YYYY-MM-DD'),
            });

            dateStart.add(1, 'days');
        }

    };

    /**
     * Divide an array into groups of size
     * 
     * @param items - Array to be divided
     * @param groupSize - Size of each group
     * @returns - Array of groups
     * Example: divideArrayIntoGroups([1, 2, 3, 4, 5, 6, 7], 3) => [[1, 2, 3], [4, 5, 6], [7]]
     */
    const divideArrayIntoGroups = (items, groupSize) => {
        const resultArray = [];
        for (let i = 0; i < items.length; i += groupSize) {
            resultArray.push(items.slice(i, i + groupSize));
        }
        return resultArray;
    };

    // get dates based on period
    const getDates = computed(() => {
        if (period.value == 'Week') {
            generateWeekDates();
            return activeCalender.value.dates;
        }
        if (period.value == 'Month') {
            generateMonthDates();
            return divideArrayIntoGroups(activeCalender.value.dates, 7);
        }
    });

    // check if today
    const isToday = (fullDate) => {
        return moment().format('YYYY-MM-DD') === fullDate;
    };

    // check if current month
    const isCurrentMonth = (fullDate) => {
        return moment(`${activeCalender.value.year}-${activeCalender.value.month}`, 'YYYY-M').format('YYYY-MM') === moment(fullDate, 'YYYY-MM-DD').format('YYYY-MM');
    };

    const generateTime = computed(() => {
        const firstHour = prebook_admin.settings.business_hours[moment().format('dddd')].hours[0]?.start;
        const lastHour = prebook_admin.settings.business_hours[moment().format('dddd')].hours[prebook_admin.settings.business_hours[moment().format('dddd')].hours.length - 1].end;

        const startTime = moment(firstHour, 'hh:mm A').get('hour');
        const endTime = moment(lastHour, 'hh:mm A').get('hour');

        let time = [];
        for (let i = startTime; i <= endTime; i++) {
            time.push(moment(i, 'HH').format('h A'));
        }

        return time;
    });
    // const generateTime = ref(['1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM', '12 AM'])

    // change month
    const changeMonth = (option) => {
        // bail, if no option is passed
        if (!option) return;

        const currentMonth = moment({
            year: activeCalender.value.year,
            month: activeCalender.value.month - 1, // Moment.js months are 0-based
        });

        // get the details of next or prev month
        const changeMonthTo = option == 'next' ? currentMonth.clone().add(1, 'month') : currentMonth.clone().subtract(1, 'month');

        activeCalender.value.month = changeMonthTo.month() + 1;
        activeCalender.value.year = changeMonthTo.year();
        activeCalender.value.dates = [];

        generateMonthDates();
    };

    // change week
    const changeWeek = (option) => {
        // bail, if no option is passed
        if (!option) return;

        if (option == 'next') {
            activeDate.value = moment(activeDate.value).add(1, 'week').format('YYYY-MM-DD');
        } else if (option == 'prev') {
            activeDate.value = moment(activeDate.value).subtract(1, 'week').format('YYYY-MM-DD');
        }

        generateWeekDates();
    };

    // change date
    const changeDate = (option) => {
        // bail, if no option is passed
        if (!option) return;

        if (option == 'next') {
            // activeDate.value = moment(activeDate.value).add(1, 'day').format('YYYY-MM-DD')
            // jump to next business day 
            let nextDate = moment(activeDate.value).add(1, 'day');
            while (isHoliday(nextDate.format('YYYY-MM-DD'))) {
                nextDate = nextDate.add(1, 'day');
            }

            activeDate.value = nextDate.format('YYYY-MM-DD');
        } else if (option == 'prev') {
            // activeDate.value = moment(activeDate.value).subtract(1, 'day').format('YYYY-MM-DD')
            // jump to previous business day
            let prevDate = moment(activeDate.value).subtract(1, 'day');
            while (isHoliday(prevDate.format('YYYY-MM-DD'))) {
                prevDate = prevDate.subtract(1, 'day');
            }

            activeDate.value = prevDate.format('YYYY-MM-DD');
        }
    };

    let currentTime = ref(moment().format('h:mm:ss A'));
    let currentHour = ref(moment().format('h A'));

    const isHoliday = (date) => {
        let day = moment(date, 'YYYY-MM-DD').format('dddd');

        // check if day is holiday
        if (!$settings.businessHours[day]?.enabled) return true;

        // check if day off from addons
        if (!$settings.addons.holidays_days_off) return false;

        // check if date is holiday
        let holiday = false;
        $settings.daysOff?.holidays.forEach(item => {
            if (holiday) return;
            if (item.find(h => h.date == date)) return holiday = true;
        });
        if (holiday) return true;

        // else
        return false;
    };

    return { activeCalender, activeDate, getDates, divideArrayIntoGroups, isToday, isCurrentMonth, generateTime, changeMonth, changeWeek, changeDate, currentTime, currentHour, isHoliday };
};
const { activeCalender, activeDate, getDates, divideArrayIntoGroups, isToday, isCurrentMonth, generateTime, changeMonth, changeWeek, changeDate, currentTime, currentHour, isHoliday } = useCalendar();

const useMoreModal = () => {
    const appointmentModalDate = ref('');

    const openAppointmentMoreModal = (date) => {
        console.log('open appointment modal', date);
        appointmentModalDate.value = date;
    };

    const closeAppointmentMoreModal = () => {
        console.log('close appointment modal');
        appointmentModalDate.value = '';
    };

    return { appointmentModalDate, openAppointmentMoreModal, closeAppointmentMoreModal };
};
const { appointmentModalDate, openAppointmentMoreModal, closeAppointmentMoreModal } = useMoreModal();

const titleDate = computed(() => {
    if (period.value === 'Month') {
        return moment(activeCalender.value.year + '-' + activeCalender.value.month, 'YYYY-MM').format('MMMM YYYY');
    } else if (period.value === 'Week') {
        return moment(getDates.value[0]?.fullDate, 'YYYY-MM-DD').format('DD MMM') + ' - ' + moment(getDates.value[getDates.value.length - 1]?.fullDate, 'YYYY-MM-DD').format('DD MMM');
    } else if (period.value === 'Day') {
        return moment(activeDate.value, 'YYYY-MM-DD').format('DD MMMM YYYY');
    }
});

const getAppointmentColorPalette = (color = '#2E90FA') => {
    let colorPalette = [
        {
            arrow: "#2E90FA",
            text: "#194185",
            background: "#F2F8FF",
            hover: "#E6F2FE",
        },
        {
            arrow: "#6172F3",
            text: "#2D3282",
            background: "#F6F7FE",
            hover: "#ECEEFE",
        },
        {
            arrow: "#875BF7",
            text: "#491C96",
            background: "#F8F5FF",
            hover: "#F8F5FF",
        },
        {
            arrow: "#D444F1",
            text: "#6F1877",
            background: "#FCF4FE",
            hover: "#FAE9FD",
        },
        {
            arrow: "#DD2590",
            text: "#851651",
            background: "#FDF2F8",
            hover: "#FBE5F2",
        },
        {
            arrow: "#EE46BC",
            text: "#851651",
            background: "#FEF4FB",
            hover: "#FDE9F7",
        },
        {
            arrow: "#FF4405",
            text: "#771A0D",
            background: "#FFF4F0",
            hover: "#FFE9E1",
        },
        {
            arrow: "#E31B54",
            text: "#89123E",
            background: "#FDF1F5",
            hover: "#FCE4EA",
        },
        {
            arrow: "#EAAA08",
            text: "#713B12",
            background: "#FEFAF0",
            hover: "#FCF5E1",
        },
        {
            arrow: "#F670C7",
            text: "#851651",
            background: "#FEF6FC",
            hover: "#FEEEF8",
        },
        {
            arrow: "#84ADFF",
            text: "#00359E",
            background: "#F8FAFF",
            hover: "#F0F5FF",
        },
        {
            arrow: "#FEA3B4",
            text: "#89123E",
            background: "#FFF9FA",
            hover: "#FFF4F6",
        },
        {
            arrow: "#FDE272",
            text: "#713B12",
            background: "#FFFDF7",
            hover: "#FFFBEE",
        },
        {
            arrow: "#EEAAFD",
            text: "#6F1877",
            background: "#FEFAFF",
            hover: "#FDF5FF",
        }
    ];

    let match = colorPalette.find(item => item.arrow === color);

    if (!match) {
        console.warn(`[prebook warn]: Color palette not found for ${color}. Using #2E90FA as default color palette.`);
        return colorPalette[0] || {};
    }

    return match;
};

const getMonthAppointments = computed(() => {
    if (!$appointment.getAppointments) return [];

    let appointments = [];

    if (localStorage.getItem('userType') == 'staff') {
        appointments = $appointment.getAppointments.filter(ap => ap.meta.status == 'approved' && ap.staff_id == prebook_admin.profile.id);
    } else {
        appointments = $appointment.getAppointments.filter(ap => ap.meta.status == 'approved');
    }


    appointments.map(ap => {
        ap.color_palette = getAppointmentColorPalette($service.getServiceById(ap.service_id)?.meta?.color);
    });

    if (filter.value.service !== 'All') appointments = appointments.filter(ap => ap.service_id == filter.value.service);
    if (filter.value.staff !== 'All') appointments = appointments.filter(ap => ap.staff_id == filter.value.staff);

    return appointments;
});

const getAppointmentsByDate = (date) => {
    const appointments = getMonthAppointments.value
        .filter(ap => ap.booking_date === date)
        .sort((a, b) => moment(a.booking_time_start, 'HH:mm').diff(moment(b.booking_time_start, 'HH:mm')));

    const more = appointments.length > 3;
    const moreCount = more ? appointments.length - 3 : 0;
    const appointmentsToReturn = more ? appointments.slice(0, 3) : appointments;

    return {
        appointments,
        more,
        moreCount,
        appointmentsToReturn
    };
};

const getAppointmentsCountForStaff = (staffId) => {
    return $appointment.getAppointments.filter(ap => {
        // Count based on filtered service and period
        if (filter.value.service !== 'All' && ap.service_id !== filter.value.service) return false;

        if (period.value == 'Month') return moment(ap.booking_date, 'YYYY-MM-DD').format('YYYY-MM') == moment(`${activeCalender.value.year}-${activeCalender.value.month}`, 'YYYY-M').format('YYYY-MM');
        if (period.value == 'Week') return moment(ap.booking_date, 'YYYY-MM-DD').isBetween(moment(getDates.value[0].fullDate, 'YYYY-MM-DD'), moment(getDates.value[getDates.value.length - 1].fullDate, 'YYYY-MM-DD'), 'day', '[]');
        if (period.value == 'Day') return ap.booking_date == activeDate.value;

    }).filter(ap => ap.staff_id == staffId).length;
};

onMounted(() => {
    activeCalender.value.month = new Date().getMonth() + 1;
    activeCalender.value.year = new Date().getFullYear();
});



const calendarAction = (option) => {
    if (!option) return;

    if (period.value == 'Month') {
        changeMonth(option);
    } else if (period.value == 'Week') {
        changeWeek(option);
    } else if (period.value == 'Day') {
        changeDate(option);
    }
};

const clickToday = () => {
    activeDate.value = moment().format('YYYY-MM-DD');
    activeCalender.value.month = new Date().getMonth() + 1;
    activeCalender.value.year = new Date().getFullYear();
};


onMounted(() => {
    setInterval(() => {
        currentTime.value = moment().format('h:mm:ss A');
        currentHour.value = moment().format('h A');
    }, 1000);
});


const calculatePosition = (app) => {
    const positions = {};

    const startTime = moment(app.booking_time_start, 'HH:mm');
    const endTime = moment(app.booking_time_end, 'HH:mm');

    const diff = endTime.diff(startTime, 'minute');

    // Calculate height
    positions.diff = diff;
    positions.height = diff * 2 + (Math.ceil(diff / 60) * 8);

    // console.log("height", diff);
    // Calculate top
    positions.top = (moment(app.booking_time_start, 'HH:mm').format('m') * 2);

    // Calculate width, divide by how many appointments overlaps in between start and end time with other appointments
    const siblings = getMonthAppointments.value.filter(a => {
        const _start = moment(a.booking_time_start, 'HH:mm');
        const _end = moment(a.booking_time_end, 'HH:mm');

        return (
            a.booking_date == app.booking_date && (
                // start time is same or end time is same.
                startTime.isSame(_start) || endTime.isSame(_end)
                || startTime.isBetween(_start, _end)
                // || endTime.isBetween(_start, _end))
                // || (_start.isBetween(startTime, endTime) 
                // || _end.isBetween(startTime, endTime))
            )
        );
    }
    );

    const width = 100 / (siblings.length || 1);

    // console.log("siblings", siblings);
    positions.width = width;

    positions.siblings = siblings.length;

    // Calculate left, sort conflictsWith by booking_time_start and find the index of current appointment
    const sortedSiblings = siblings.sort((a, b) => {
        // large duration first
        return moment(a.booking_time_start, 'HH:mm').diff(moment(b.booking_time_start, 'HH:mm'));
    });
    const indexOfCurrentAppointment = sortedSiblings.findIndex(a => a.appointment_id == app.appointment_id);

    positions.left = (indexOfCurrentAppointment) * width;

    // Z-index.
    positions.zIndex = (indexOfCurrentAppointment + 1) * 10;

    return positions;
};

const staffs = computed(() => {
    // check if staff
    let isStaff = localStorage.getItem('userType') == 'staff' || false;

    // get staff based on user type
    let s = isStaff ? $staff.getStaffs.filter(staff => staff.staff_id == prebook_admin.profile.id) : $staff.getStaffs;

    // add service count
    let mapStaffs = s.map(staff => {
        staff.serviceCount = getAppointmentsCountForStaff(staff.staff_id);
        return staff;
    });

    // sort by service count
    let _staffs = isStaff ? mapStaffs : mapStaffs.filter(staff => staff.serviceCount > 0).sort((a, b) => b.serviceCount - a.serviceCount);

    return _staffs;
});

const showAllStaffs = () => {
    return localStorage.getItem('userType') == 'staff' ? false : true;
}

</script>

<template>
<div class="relative my-[-60px] mx-[-20px] flex">
    <!-- left content -->
    <div class="overflow-x-scroll md:w-full"
        :class="$settings.addons.staffs ? 'xl:h-[calc(100vh-100px)] md:!w-[calc(100%-85px)] lg:!w-[calc(100%-112px)]' : 'md:w-full'">
        <!-- header -->
        <div class="flex-col lg:flex-row px-5 py-4 flex lg:items-center justify-between mb-[23px]">
            <div class="flex items-center gap-2 md:gap-4">
                <div class="text-[#101828] font-semibold text-lg md:text-2xl">{{ titleDate }}</div>
                <div class="flex items-center gap-2" :class="{ 'flex-row-reverse': $settings.isRTL }">
                    <Icon @click="calendarAction('prev')" name="chevronLeft" size="34px"
                        class="cursor-pointer rounded-full border border-[#EAECF0] text-[#667085] p-[5px] transition-all duration-300 hover:bg-[#EAECF0]">
                    </Icon>
                    <div @click="clickToday"
                        class="cursor-pointer rounded-full border border-[#EAECF0] text-[#667085] py-2 px-5 text-sm font-semibold">
                        Today</div>
                    <Icon @click="calendarAction('next')" name="chevronRight" size="34px"
                        class="cursor-pointer rounded-full border border-[#EAECF0] text-[#667085] p-[5px] transition-all duration-300 hover:bg-[#EAECF0]">
                    </Icon>
                </div>
            </div>

            <div class="flex flex-col md:flex-row-reverse items-center justify-end lg:justify-normal gap-2 mt-2 lg:mt-0"
                :class="{ 'flex-row-reverse': $settings.isRTL }">
                <Select :options="serviceOptions" v-model="filter.service"
                    :placeholder="`Select ${$settings.labels.service?.singular}`" search
                    selectionContainer="py-[10px] px-[14px] w-full font-semibold border-[#D0D5DD]"
                    class="w-full md:w-[191px]"></Select>
                <div class="flex gap-2 items-center rounded-lg border border-[#F2F4F7] bg-[#F9FAFB] p-1">
                    <div v-for="item in periods"
                        class="cursor-pointer rounded-lg text-[#667085] py-2 px-3 text-sm font-semibold w-[102px] text-center transition-all duration-300 hover:text-[#344054] hover:bg-[#FFF] hover:shadow-[0px_1px_2px_0px_#1018280F,0px_1px_3px_0px_#1018281A]"
                        :class="{ 'bg-[#FFF] !text-[#344054] shadow-[0px_1px_2px_0px_#1018280F,0px_1px_3px_0px_#1018281A]': period === item }"
                        @click="changeTab(item)">{{ item }}</div>
                </div>
            </div>
        </div>

        <div class="overflow-x-scroll lg:overflow-visible">
            <!-- Day tab -->
            <div class="mt-2 lg:mt-0" v-if="period == 'Day'">
                <div v-for="(hour, index) in generateTime" :key="index" class="relative w-full">
                    <!-- timer line -->
                    <div v-if="hour == currentHour && isToday(activeDate)"
                        :style="`top:${moment(currentTime, 'h:mm:ss A').format('m') * 2}px`"
                        class="current-time-bar absolute right-0 w-[calc(100%-73px)] h-[2px] bg-[#9E77ED] z-20">
                        <div class="absolute -left-1 -top-[3px] w-2 h-2 bg-[#9E77ED] rounded-full"></div>
                        <div class="absolute text-[#9E77ED] text-[12px] font-semibold left-[-57px] top-[-7px]">{{
                            moment(currentTime, 'h:mm:ss A').format('h:mm A') }}</div>
                    </div>

                    <div class="flex w-full">
                        <!-- first blank box -->
                        <div class="w-[73px] h-[128px] relative bg-[linear-gradient(270deg,#EAECF0_10%,transparent_30%)]"
                            :class="[index == 0 ? 'p-[1px_0px_1px_0px]' : 'p-[0px_0px_1px_0px]']">
                            <div
                                class="absolute -translate-x-2/4 top-[-9px] left-2/4 text-[#98A2B3] text-[12px] font-medium">
                                {{ hour }}</div>
                            <div class="w-[73px] h-full bg-white"></div>
                        </div>

                        <!-- list box -->
                        <div class="w-full h-[calc(120px+8px)] bg-[#EAECF0]"
                            :class="[index == 0 ? 'p-[1px_0px_1px_1px]' : 'p-[0px_0px_1px_1px]']">
                            <div class="relative w-full h-full p-1 bg-white"
                                :style="[isHoliday(activeDate) ? `background-image:url(${Image('calendar-holiday-dots.png')}); background-size:contain;` : '']">
                                <template v-for="list in getAppointmentsByDate(activeDate).appointments">
                                    <div
                                        v-if="isDetailsModalActive && activeAppointmentId == list.appointment_id && moment(list.booking_time_start, 'HH:mm').format('h A') == hour">
                                        <DetailsModal :appointmentId="activeAppointmentId" @close="closeDetailsModal"
                                            :style="`top: ${calculatePosition(list).top + 5}px; ${calculateDetailsModalPosition(0, 0, period)}`" />
                                        <svg class="absolute transition-all duration-300  z-[2]"
                                            xmlns="http://www.w3.org/2000/svg" width="11" height="21"
                                            viewBox="0 0 11 21" fill="none"
                                            :style="`top: ${calculatePosition(list).top + 15}px; ${calculateArrowPosition(0, period).style}`">
                                            <defs>
                                                <filter id="shadow-normal" x="0" y="0">
                                                    <feDropShadow dx="-1" dy="0" stdDeviation="1"
                                                        flood-color="#99999999" flood-opacity="1" />
                                                </filter>
                                            </defs>
                                            <path
                                                d="M1.38553 12.7039C0.0997374 11.5162 0.0997363 9.48438 1.38553 8.29661L10.25 0.107971L10.25 20.8926L1.38553 12.7039Z"
                                                fill="white" filter="url(#shadow-normal)" />
                                        </svg>
                                    </div>

                                    <div v-if="moment(list.booking_time_start, 'HH:mm').format('h A') == hour"
                                        @click="openDetailsModal(list.appointment_id)"
                                        class="opacity-95 absolute border-2 clear-both z-[1] cursor-pointer flex gap-1 items-center text-[12px] transition p-1 rounded hover:shadow-md hover:z-30 hover:opacity-100"
                                        :style="`width: ${calculatePosition(list).width}%; height: ${calculatePosition(list).height}px; top: ${calculatePosition(list).top}px; left: ${calculatePosition(list).left}%; z-index: ${calculatePosition(list).zIndex}} !important; background:${list.color_palette?.background}; color:${list.color_palette?.text}; border-color: ${list.color_palette?.arrow}`">
                                        <!-- {{ calculatePosition(list) }} -->
                                        <div class="absolute top-0 left-0 w-0 h-0 border-t-[8px] border-r-[8px] border-r-transparent"
                                            :style="`border-top-color: ${list.color_palette?.arrow};`"></div>
                                        <div class="bg-white absolute top-[2px] skew-y-[-46deg] w-2.5 h-1 left-0"></div>
                                        <div class="truncate font-medium w-[calc(100%-38px)]">{{ list.meta?.service_name
                                            }}</div>
                                        <div class="ml-auto text-nowrap">{{ moment(list.booking_time_start,
                                            'HH:mm').format('h:mm A') }}</div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Week tab -->
            <div class="w-[1343px] lg:w-full" v-if="period == 'Week'">
                <!-- table header -->
                <div class="flex w-full">
                    <div class="bg-[linear-gradient(270deg,#EAECF0_10%,transparent_30%)] p-[0px_0px_1px_0px] w-[73px]">
                        <div class="w-[73px] h-[65px] bg-white"></div>
                    </div>
                    <div v-for="item in getDates"
                        class="bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_0px_1px_1px] w-[calc((100%-73px)/7)]">
                        <!-- {{ item }} -->
                        <div
                            class="h-[65px] text-center py-[12px] bg-white text-[12px] font-medium text-[#667085] flex flex-col justify-center">
                            <div>{{ item.day.slice(0, 3) }}</div>
                            <div class="m-auto rounded-full w-[26px] h-[26px] flex items-center justify-center leading-normal"
                                :class="{ 'bg-[#7F56D9] text-white': isToday(item.fullDate) }">{{ item.date }}</div>
                        </div>
                    </div>
                </div>

                <!-- table content -->
                <div v-for="(hour, index) in generateTime" :key="index" class="relative w-full">

                    <div class="flex w-full">
                        <!-- first blank box -->
                        <div
                            class="w-[73px] relative bg-[linear-gradient(270deg,#EAECF0_10%,transparent_30%)] p-[0px_0px_1px_0px]">
                            <div
                                class="absolute -translate-x-2/4 top-[-9px] left-2/4 text-[#98A2B3] text-[12px] font-medium">
                                {{
                                    hour }}</div>
                            <div class="w-[73px] h-full bg-white"></div>
                        </div>

                        <!-- list box -->
                        <div v-for="(date, i) in getDates"
                            class="relative w-[calc((100%-73px)/7)] h-[calc(120px+8px)] bg-[#EAECF0] p-[0px_0px_1px_1px]"
                            :data-date="date.fullDate" :data-hour="hour">

                            <!-- timer line -->
                            <div v-if="hour == currentHour && isToday(date.fullDate)"
                                :style="`top:${moment(currentTime, 'h:mm:ss A').format('m') * 2}px`"
                                class="current-time-bar absolute left-0 right-0 w-full h-[2px] bg-[#9E77ED] z-20">
                                <div class="absolute -left-1 -top-[3px] w-2 h-2 bg-[#9E77ED] rounded-full"></div>
                                <!-- <div class="absolute text-[#9E77ED] text-[12px] font-semibold left-[-57px] top-[-7px]">{{ moment(currentTime, 'h:mm:ss A').format('h:mm A') }}</div> -->
                            </div>

                            <div class="relative w-full h-full p-1 bg-white"
                                :style="[isHoliday(date.fullDate) ? `background-image:url(${Image('calendar-holiday-dots.png')})` : '']">
                                <template v-for="list in getAppointmentsByDate(date.fullDate).appointments">
                                    <div v-if="moment(list.booking_time_start, 'HH:mm').format('h A') == hour"
                                        class="relative w-full">

                                        <DetailsModal
                                            v-if="isDetailsModalActive && activeAppointmentId == list.appointment_id"
                                            :appointmentId="activeAppointmentId" @close="closeDetailsModal"
                                            :style="calculateDetailsModalPosition(index, i, period)" />

                                        <div @click="openDetailsModal(list.appointment_id)"
                                            class="overflow-hidden flex-wrap border-white border-2 cursor-pointer absolute flex gap-1 items-center text-[12px] p-1 rounded hover:shadow z-10 hover:z-30 transition-all duration-300"
                                            :style="`height: ${calculatePosition(list).height}px; width: ${calculatePosition(list).with}% !important; top: ${calculatePosition(list).top}px; left: ${calculatePosition(list).left}%; background: ${list.color_palette?.background}; color: ${list.color_palette?.text}; border-color: ${list.color_palette?.arrow};`">
                                            <svg v-if="isDetailsModalActive && activeAppointmentId == list.appointment_id"
                                                class="absolute transition-all duration-300  z-[2]"
                                                :style="calculateArrowPosition(i, period)?.style"
                                                xmlns="http://www.w3.org/2000/svg" width="11" height="21"
                                                viewBox="0 0 11 21" fill="none">
                                                <defs>
                                                    <filter id="shadow-normal" x="0" y="0">
                                                        <feDropShadow dx="-1" dy="0" stdDeviation="1"
                                                            flood-color="#99999999" flood-opacity="1" />
                                                    </filter>
                                                    <filter id="shadow-rotated" x="0" y="0">
                                                        <feDropShadow dx="-1" dy="-1" stdDeviation="1"
                                                            flood-color="#99999999" flood-opacity="1" />
                                                    </filter>
                                                </defs>
                                                <path
                                                    d="M1.38553 12.7039C0.0997374 11.5162 0.0997363 9.48438 1.38553 8.29661L10.25 0.107971L10.25 20.8926L1.38553 12.7039Z"
                                                    fill="white"
                                                    :filter="calculateArrowPosition(i, period)?.isRotated ? 'url(#shadow-rotated)' : 'url(#shadow-normal)'" />
                                            </svg>
                                            <div class="absolute top-0 left-0 w-0 h-0 border-t-[8px] border-r-[8px] border-r-transparent"
                                                :style="`border-top-color:${list.color_palette?.arrow}`"></div>
                                            <div class="bg-white absolute top-[2px] skew-y-[-46deg] w-2.5 h-1 left-0">
                                            </div>
                                            <div class="truncate font-medium text-wrap">{{ list.meta?.service_name }}
                                            </div>
                                            <div class="ml-auto text-wrap">{{ moment(list.booking_time_start,
                                                'HH:mm').format('h:mm A') }}</div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Month tab -->
            <div class="w-[1343px] lg:w-full" v-if="period == 'Month'">
                <!-- table header -->
                <div class="sticky top-0 z-40 flex w-full bg-white">
                    <div v-for="day in sortedDays()"
                        class="bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_0px] last:p-[0px_0px_1px_0px] w-[calc(100%/7)]">
                        <div class="h-[39px] text-center py-[12px] bg-white text-[12px] font-medium text-[#667085]">
                            {{ day.slice(0, 3) }}
                        </div>
                    </div>
                </div>

                <!-- table content -->
                <div v-for="(row, index) in getDates" :key="index" class="flex items-center justify-between w-full">
                    <div v-for="(item, i) in row"
                        class="w-[calc(100%/7)] h-[150px] border-b border-r last:border-r-0 border-[#EAECF0] relative"
                        :style="[isHoliday(item.fullDate) ? `background-image:url(${Image('calendar-holiday-dots.png')})` : '']">
                        <!-- more modal -->
                        <MoreListModal v-if="appointmentModalDate == item.fullDate"
                            :items="getAppointmentsByDate(appointmentModalDate)" @close="closeAppointmentMoreModal"
                            :row_index="index" :col_index="i" :period="period" :class="{
                                'left-0 -translate-y-2/4': i == 0,
                                'right-0 -translate-y-2/4': i == 6,
                                'left-0 -translate-y-2/4 -translate-x-2/4': i != 0 && i != 6
                            }" />

                        <!-- date -->
                        <div class="py-2 px-[12px] text-sm font-semibold flex items-center justify-end gap-2"
                            :class="[isCurrentMonth(item.fullDate) ? 'text-[#344054]' : 'text-[#98A2B3]']">
                            <div v-if="item.date == 1" class="font-normal uppercase">{{ moment(item.fullDate,
                                'YYYY-MM-DD').format('MMM') }}</div>
                            <div class="rounded-full w-[26px] h-[26px] flex items-center justify-center leading-normal"
                                :class="{ 'bg-[#7F56D9] text-white': isToday(item.fullDate) }">{{ item.date }}</div>
                        </div>

                        <!-- list -->
                        <div class="flex flex-col gap-1 p-3">

                            <div v-for="list in getAppointmentsByDate(item.fullDate).appointmentsToReturn"
                                class="relative">
                                <DetailsModal v-if="isDetailsModalActive && activeAppointmentId == list.appointment_id"
                                    :appointmentId="activeAppointmentId" @close="closeDetailsModal"
                                    :style="calculateDetailsModalPosition(index, i, period)" />
                                <div @click="openDetailsModal(list.appointment_id)"
                                    class="relative flex gap-1 items-center transition-all duration-300 hover:bg-[#F2F4F7] py-0.5 px-1 text-[12px] cursor-pointer">
                                    <svg v-if="isDetailsModalActive && activeAppointmentId == list.appointment_id"
                                        class="absolute transition-all duration-300  z-[2]"
                                        :style="calculateArrowPosition(i, period)?.style"
                                        xmlns="http://www.w3.org/2000/svg" width="11" height="21" viewBox="0 0 11 21"
                                        fill="none">
                                        <defs>
                                            <filter id="shadow-normal" x="0" y="0">
                                                <feDropShadow dx="-1" dy="0" stdDeviation="1" flood-color="#99999999"
                                                    flood-opacity="1" />
                                            </filter>
                                            <filter id="shadow-rotated" x="0" y="0">
                                                <feDropShadow dx="-1" dy="-1" stdDeviation="1" flood-color="#99999999"
                                                    flood-opacity="1" />
                                            </filter>
                                        </defs>
                                        <path
                                            d="M1.38553 12.7039C0.0997374 11.5162 0.0997363 9.48438 1.38553 8.29661L10.25 0.107971L10.25 20.8926L1.38553 12.7039Z"
                                            fill="white"
                                            :filter="calculateArrowPosition(i, period)?.isRotated ? 'url(#shadow-rotated)' : 'url(#shadow-normal)'" />
                                    </svg>
                                    <div class="h-1 w-1 rounded-full"
                                        :style="`background-color: ${list.color_palette?.arrow}`">
                                    </div>
                                    <div class="truncate text-[#101828] font-medium w-[calc(100%-64px)]">{{
                                        list.meta?.service_name
                                    }}</div>
                                    <div class="text-nowrap ml-auto text-[#667085]">{{ moment(list.booking_time_start,
                                        'HH:mm').format('h:mm A') }}</div>
                                </div>
                            </div>
                            <div v-if="getAppointmentsByDate(item.fullDate).more"
                                @click="openAppointmentMoreModal(item.fullDate)"
                                class="prebook-appointment-more-button text-[#101828] font-semibold text-[11px] cursor-pointer py-0.5 px-1">
                                {{ getAppointmentsByDate(item.fullDate).moreCount }} More</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
    <!-- right content -->
    <div v-if="$settings.addons.staffs"
        class="hidden md:flex relative bg-[#F9FAFB] border-l border-[#EAECF0] px-4 lg:px-8 py-4 w-[85px] lg:w-[112px] gap-[14px] flex-col">
        <!-- <div class="overflow-y-scroll h-[calc(100vh-88px)]"> -->
        <div v-if="showAllStaffs()" @click="filter.staff = 'All'"
            :class="{ 'grayscale bg-[#98A2B3] transition-all duration-300 hover:bg-[#977fca]': filter.staff !== 'All', 'bg-[#7F56D9] ': filter.staff == 'All' }"
            class="w-[48px] h-[48px] rounded-full  flex items-center justify-center text-[#FFF] text-sm font-semibold cursor-pointer transition">
            All</div>

        <div @click="filter.staff = staff.staff_id" v-for="staff in staffs"
            :class="{ 'grayscale ring-transparent transition-all duration-300 hover:ring-[#7F56D9]': filter.staff !== staff.staff_id, 'ring-[#7F56D9]': filter.staff === staff.staff_id }"
            class="ring flex items-center justify-center w-[48px] h-[48px] rounded-full bg-cover bg-no-repeat cursor-pointer relative transition-all duration-300 hover:grayscale-0">
            <div
                class="z-30 absolute -bottom-1 -right-1 bg-[#7F56D9] text-[#FFF] rounded-full py-0.5 px-1.5 text-[12px] font-medium">
                {{ staff.serviceCount }}</div>
            <ProfilePicture :name="staff?.name" :img="staff?.meta?.profile_image_url" :bgColor="staff?.meta?.color"
                class="w-full h-full" />
        </div>
        <!-- </div> -->
    </div>
</div>
</template>


<style></style>